import { z } from 'zod'

export const RoleSchema = z.enum(['admin', 'editor'])
export type Role = z.infer<typeof RoleSchema>

export const LoginRequestSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(200),
  /** Cloudflare Turnstile token from the sign-in form (#228). */
  turnstileToken: z.string().max(2048).optional(),
})
export type LoginRequest = z.infer<typeof LoginRequestSchema>

export const PASSWORD_MIN_LENGTH = 6

export const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `At least ${PASSWORD_MIN_LENGTH} characters`)
  .max(200)

export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string(),
  /** Built-in role. A user with a custom role is an `editor` underneath. */
  role: RoleSchema,
  /** Name of the custom role, when the user has one. */
  roleName: z.string().nullable().default(null),
  /** Everything the user may do, as `<area>:<action>`. */
  permissions: z.array(z.string()).default([]),
})
export type SessionUser = z.infer<typeof SessionUserSchema>

export const MeResponseSchema = z.object({ user: SessionUserSchema })

// Two-factor sign-in (#227): an authenticator app (TOTP) plus ten recovery codes, per Worker behind TWO_FACTOR.

/** After a correct password: a code, or setting 2FA up first (required and enforced). */
export const TwoFactorStepSchema = z.enum(['code', 'setup'])
export type TwoFactorStep = z.infer<typeof TwoFactorStepSchema>

/**
 * POST /auth/login: a session, or the next step of a two-factor sign-in. The pending step itself is in an HttpOnly
 * cookie (5 minutes, used once); no session exists until the code passes.
 */
export const LoginResponseSchema = z.union([MeResponseSchema, z.object({ twoFactor: TwoFactorStepSchema })])

/** Six digits from the app, or a recovery code (`abcde-fghjk`). */
export const TwoFactorCodeRequestSchema = z.object({ code: z.string().trim().min(6).max(32) })

/** A new authenticator key: the QR code holds `uri`; `secret` is the same key to type in by hand. */
export const TwoFactorSetupSchema = z.object({ secret: z.string(), uri: z.string() })

/** Set-up confirmed: the recovery codes (shown once), and the session user when set-up finished a sign-in. */
export const TwoFactorConfirmResponseSchema = z.object({ recoveryCodes: z.array(z.string()), user: SessionUserSchema.optional() })

export const TwoFactorModeSchema = z.enum(['off', 'grace', 'enforce'])

/** The signed-in person's 2FA: whether it is on, required of them, and how the switch is set. */
export const TwoFactorStatusSchema = z.object({
  mode: TwoFactorModeSchema,
  required: z.boolean(),
  enabled: z.boolean(),
  recoveryCodesLeft: z.number().int(),
})
export type TwoFactorStatusDto = z.infer<typeof TwoFactorStatusSchema>

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: PasswordSchema,
})

export const AdminUserSchema = SessionUserSchema.extend({
  roleId: z.string().nullable().default(null),
  /** Two-factor sign-in is on (#227). Null when this site can't tell: central sign-in keeps it in bak-accounts. */
  twoFactor: z.boolean().nullable().default(null),
  active: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
})
export type AdminUser = z.infer<typeof AdminUserSchema>

export const CreateUserRequestSchema = z.object({
  email: z.email().max(254),
  name: z.string().min(1).max(120),
  role: RoleSchema,
  /** A custom role (Advanced and Store); the built-in role is then `editor`. */
  roleId: z.string().nullable().default(null),
  /**
   * The first password, set by the administrator. Required unless the site uses central sign-in (#228 step 3): there
   * the person sets their own through a one-time link, or already has a Digimasi account.
   */
  password: PasswordSchema.optional(),
})

/** A link where someone sets their own Digimasi sign-in password (central sign-in, #228 step 3). */
export const SetPasswordLinkSchema = z.object({ url: z.url(), expiresAt: z.string() })
export type SetPasswordLinkDto = z.infer<typeof SetPasswordLinkSchema>

export const CreateUserResponseSchema = z.object({
  id: z.string(),
  /**
   * Central sign-in only: the one-time link for the person, shown once to the administrator who added them. It looks
   * the same whether or not the email already had a Digimasi account (the site is never told which).
   */
  setPasswordLink: SetPasswordLinkSchema.nullable().optional(),
})

export const UpdateUserRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  role: RoleSchema.optional(),
  roleId: z.string().nullable().optional(),
  active: z.boolean().optional(),
})
